import { api } from '@/lib/api';

export interface OnlineBookingRow {
  appointmentId: string;
  submittedName: string;
  rawPhone: string;
  phoneE164: string;
  createdAt: string;
  version: number;
  overdue: boolean;
  handler?: { id: string; fullNameAr: string; fullNameEn?: string } | null;
  claimHolder?: { id: string; fullNameAr: string; fullNameEn?: string } | null;
  appointment: {
    id: string;
    status: string;
    startAt: string;
    endAt: string;
    providerId: string;
    provider?: { nameAr: string; nameEn?: string } | null;
    procedures?: Array<{
      procedure?: { nameAr: string; nameEn?: string } | null;
      category?: { id: string; nameAr: string; nameEn?: string | null } | null;
    }>;
    onlineBookingPolicyVersion?: { service?: { id: string; code: string; nameAr: string; nameEn?: string | null } | null } | null;
    patientId?: string | null;
    patient?: { id: string; fileNumber: string; firstName?: string | null; fatherName?: string | null; grandfatherName?: string | null; familyName?: string | null } | null;
  };
  service?: { id?: string; code?: string; nameAr?: string; nameEn?: string | null } | Array<{ nameAr?: string; nameEn?: string | null } | null | undefined> | null;
}

export interface OnlineBookingDetail extends OnlineBookingRow {
  email?: string | null;
  notes?: string | null;
  service?: Array<{ nameAr?: string; nameEn?: string | null } | null | undefined> | { code?: string; nameAr?: string; nameEn?: string | null } | null;
  requestedSlot: { startAt: string; endAt: string };
  conflictCheck: { hardBlocks?: unknown[]; warnings?: unknown[] };
  patientMatches: Array<{ id: string; fileNumber?: string; firstName?: string; fatherName?: string; grandfatherName?: string; familyName?: string; phoneE164?: string; lastVisitAt?: string | null }>;
  earlierRequests: Array<{ appointmentId: string; createdAt: string; appointment: { status: string; startAt: string } }>;
  contactAttempts: Array<{ id: string; outcome: string; note?: string | null; createdAt: string; user?: { fullNameAr: string; fullNameEn?: string | null } }>;
  lead?: { status: string; bookingRequests?: Array<{ appointmentId: string; createdAt: string; patientResolution?: string; appointment?: { status: string } }> } | null;
  claimExpiresAt?: string | null;
}

export interface OnlineBookingPage {
  data: OnlineBookingRow[];
  serverNow: string;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export type OnlineBookingLeadStatus = 'NEW' | 'CONTACTED' | 'LOST';
export interface OnlineBookingLead {
  id: string;
  name: string;
  phoneE164: string;
  email?: string | null;
  notes?: string | null;
  sourcePage?: string | null;
  locale: string;
  status: OnlineBookingLeadStatus;
  createdAt: string;
  patientId?: string | null;
  patient?: {
    id: string;
    fileNumber: string;
    firstName: string;
    fatherName?: string | null;
    grandfatherName?: string | null;
    familyName?: string | null;
  } | null;
}
export interface OnlineBookingLeadPage {
  data: OnlineBookingLead[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface OnlineBookingServiceOption {
  id: string;
  code: string;
  nameAr: string;
  nameEn?: string | null;
}

export interface OnlineBookingAvailability {
  providers: Array<{ id: string; nameAr: string; nameEn?: string | null }>;
  slots: Array<{ providerId: string; startAt: string; endAt: string }>;
  serverNow: string;
}

export const onlineBookingApi = {
  list: (params: { tab: 'pending' | 'overdue' | 'handled'; serviceId?: string; date?: string; page?: number; pageSize?: number }) =>
    api.get<OnlineBookingPage>('/online-booking/requests', { params }).then((r) => r.data),
  leads: (params: { status?: OnlineBookingLeadStatus; page?: number; pageSize?: number }) =>
    api.get<OnlineBookingLeadPage>('/online-booking/leads', { params }).then((r) => r.data),
  leadCounts: () => api.get<{ new: number }>('/online-booking/leads/counts').then((r) => r.data),
  updateLeadStatus: (id: string, status: OnlineBookingLeadStatus) =>
    api.patch<{ id: string; status: OnlineBookingLeadStatus }>(`/online-booking/leads/${id}`, { status }).then((r) => r.data),
  counts: () => api.get<{ pending: number; overdue: number; serverNow: string }>('/online-booking/requests/counts').then((r) => r.data),
  services: () => api.get<OnlineBookingServiceOption[]>('/online-booking/requests/services').then((r) => r.data),
  detail: (id: string) => api.get<OnlineBookingDetail>(`/online-booking/requests/${id}`).then((r) => r.data),
  availability: (id: string, date: string, providerId?: string) =>
    api.get<OnlineBookingAvailability>(`/online-booking/requests/${id}/availability`, { params: { date, providerId } }).then((r) => r.data),
  claim: (id: string) => api.post(`/online-booking/requests/${id}/claim`, {}),
  release: (id: string) => api.delete(`/online-booking/requests/${id}/claim`, { data: {} }),
  resolvePatient: (id: string, body: { version: number; kind: 'link'; patientId: string } | { version: number; kind: 'createNew' }) =>
    api.post(`/online-booking/requests/${id}/patient-resolution`, body).then((r) => r.data),
  contactAttempt: (id: string, body: { version: number; outcome: string; note?: string }) =>
    api.post(`/online-booking/requests/${id}/contact-attempts`, body).then((r) => r.data),
  confirm: (id: string, body: { version: number; startAt?: string; providerId?: string; note?: string }) =>
    api.post(`/online-booking/requests/${id}/confirm`, body).then((r) => r.data),
  reject: (id: string, body: { version: number; reasonId: string; note?: string }) =>
    api.post(`/online-booking/requests/${id}/reject`, body).then((r) => r.data),
};

export const onlineBookingKeys = {
  all: ['online-booking'] as const,
  list: (tab: string, serviceId?: string, date?: string, pageSize?: number) => ['online-booking', 'list', tab, serviceId, date, pageSize] as const,
  counts: ['online-booking', 'counts'] as const,
  leads: (status?: string, page?: number) => ['online-booking', 'leads', status, page] as const,
  leadCounts: ['online-booking', 'lead-counts'] as const,
  detail: (id: string) => ['online-booking', 'detail', id] as const,
};
